import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import {
  createAdHocPrep,
  fetchAccountPrep,
} from "@/services/frontendServices/prepServices";
import type { CreateAdHocPrepInput } from "@/schemas";
import type { AccountPrepResponseDto, MeetingPrepDto } from "@/types";

import { myScheduleKeys } from "./useMySchedule";
import { prepKeys } from "./usePrep";

export const accountPrepKeys = {
  all: ["accountPrep"] as const,
  detail: (accountId: string) => [...accountPrepKeys.all, accountId] as const,
};

/** The account's active ad hoc prep, or null — drives the Start Prep button. */
export const useAccountPrep = (
  accountId: string,
  options?: Partial<UseQueryOptions<AccountPrepResponseDto, Error>>
) =>
  useQuery<AccountPrepResponseDto, Error>({
    queryKey: accountPrepKeys.detail(accountId),
    queryFn: () => fetchAccountPrep(accountId),
    enabled: !!accountId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
    ...options,
  });

/**
 * Variables of useCreateAdHocPrep. `accountId` rides in the form data rather
 * than the hook call so the hook fits CreateForm's no-argument
 * `createMutationHook` contract, the same way useCreateContact does.
 */
export type CreateAdHocPrepFormData = CreateAdHocPrepInput & {
  accountId: string;
};

/** Opens the account's active ad hoc prep, creating one when none exists. */
export const useCreateAdHocPrep = () => {
  const queryClient = useQueryClient();

  return useMutation<MeetingPrepDto, Error, CreateAdHocPrepFormData>({
    mutationFn: ({ accountId, ...payload }) =>
      createAdHocPrep(accountId, payload),
    onSuccess: (prep, { accountId }) => {
      // Seed the prep cache so the navigation lands on warm data.
      queryClient.setQueryData(prepKeys.detail(`adhoc:${prep.id}`), prep);
      queryClient.invalidateQueries({
        queryKey: accountPrepKeys.detail(accountId),
      });
      queryClient.invalidateQueries({ queryKey: myScheduleKeys.all });
    },
  });
};
